import { createApi } from "@reduxjs/toolkit/query/react";
import { axiosBaseQuery } from "./helpers/base-query";
import { hitlApiInstance } from "./axios/api-instances";
import { Money } from "./types";

const baseQuery = axiosBaseQuery(hitlApiInstance);

export const hitlApi = createApi({
  reducerPath: "hitlApi",
  baseQuery,
  endpoints: (builder) => ({
    latestActiveCheckout: builder.query<CheckoutResponse, string>({
      query: (zoneId) => ({
        url: `/client/zone/${zoneId}/checkout`,
        method: "GET",
      }),
    }),
    checkout: builder.query<CheckoutResponse, string>({
      query: (key) => ({ url: `/client/checkout/${key}`, method: "GET" }),
    }),
    message: builder.query<MessageResponse, string>({
      query: (key) => ({
        url: `/client/checkout/${key}/message`,
        method: "GET",
      }),
    }),

    closeCheckout: builder.mutation<void, string>({
      query: (key) => ({
        url: `/client/checkout/${key}/close`,
        method: "POST",
      }),
    }),
    cancelCheckout: builder.mutation<void, string>({
      query: (key) => ({
        url: `/client/checkout/${key}/cancel`,
        method: "POST",
      }),
    }),
    sendMessage: builder.mutation<void, { key: string; message_codes: string[] }>({
      query: ({ key, message_codes }) => ({
        url: `/client/checkout/${key}/message`,
        method: "POST",
        data: {
          key,
          message_codes,
        },
      }),
    }),
    state: builder.query<StateResponse, string>({
      query: () => ({
        url: `/ai_hitlman/state`,
        method: "POST",
      }),
    }),

    createCart: builder.mutation<CreateCartResponse, CreateCartVariables>({
      query: ({ checkoutId }) => ({
        url: `/client/checkout/${checkoutId}/create_cart`,
        method: "POST",
      }),
    }),
    createAutomatedCheckout: builder.mutation<{ result: { key: string } }, { zoneId: string }>({
      query: ({ zoneId }) => ({
        url: `/v2/client/zone/${zoneId}/checkout`,
        method: "POST",
      }),
    }),
  }),
});

export const {
  useMessageQuery,
  useCheckoutQuery,
  useCloseCheckoutMutation,
  useCancelCheckoutMutation,
  useSendMessageMutation,
  useStateQuery,
  useLazyStateQuery,
  useLatestActiveCheckoutQuery,
  useLazyLatestActiveCheckoutQuery,
  useCreateCartMutation,
  useCreateAutomatedCheckoutMutation,
} = hitlApi;

export type CheckoutResponse = {
  result: {
    id: string;
    lines: {
      variant: {
        id: string;
        name: string;
        sku: string;
        image_url: string;
        price_with_tax: Money;
        price_before_tax: Money;
        tax_price: Money;
      };
      quantity: number;
      price_with_tax: Money;
      price_before_tax: Money;
      tax_price: Money;
    }[];
    price_with_tax: Money;
    price_before_tax: Money;
    tax_price: Money;
  };
};

export type MessageResponse = {
  result: {
    message_codes: string[];
    image_width?: number;
    image_height?: number;
    polygons?: [number, number][][];
  } | null;
};

export type StateResponse = {
  result: {
    checkout_id: string;
    is_test: false;
    variants: [
      {
        id: string;
        name: string;
        sku: string;
        image_url: string;
      }
    ];
    polygons: [number, number][][];
    image_id: string;
    polygon_finder_parameters: unknown;
  };
};

export type CreateCartResponse = {
  result: string;
};
export type CreateCartVariables = {
  checkoutId: string;
};
